// How the audit log draws one entry: its tone and its icon.
//
// Red is for destructive events only. It used to follow the family, so every
// `user.*` row was red — accepting an invite and issuing a password reset read
// as alarming as deleting someone — and the icon followed the second half of
// the action, matched against suffixes the server never writes (`edited`,
// `launched`), so most rows fell through to a generic "•••".
//
// The actions the server writes (api/src, 2026-10-12) — keep this list and the
// table below in step when a route gains an audit row:
//   user.invited · user.invite_resent · user.invite_accepted · user.patched
//   user.enabled · user.disabled · user.deleted
//   user.password_reset_issued · user.password_reset_completed
//   campaign.created · campaign.plan_updated · campaign.duplicated
//   campaign.updated · campaign.ready_to_publish · campaign.auto_launch_armed
//   campaign.auto_launch_disarmed · campaign.paused · campaign.resumed
//   campaign.archived · campaign.deleted · campaign.sync_retry_requested
//   campaign.creative_rollout_requested · campaign.creative_added
//   campaign.creative_addition_retried · campaign.creative_addition_cancelled
//   creative.created · creative.updated · creative.content_saved
//   creative.revision_created · creative.asset_attached · creative.asset_detached
//   creative.submitted_for_review · creative.approved · creative.archived
//   creative.restored · creative.deleted · creative.meta_previews_refreshed
//   creative.review_link_issued · creative.review_link_revoked
//   creative.review_package_issued · creative.sync_retry_requested
//   audience.created · audience.updated · audience.refresh_requested
//   audience.archived · audience.restored
//   pixel.created · pixel.updated · pixel.conversions_updated · pixel.archived
//   pixel.duplicated · pixel.share_link_issued · pixel.share_revoked
//   pixel.server_key_minted · pixel.server_key_rotated · pixel.server_key_revoked
//   alert.acked · alert.resolved
//   alert_rule.created · alert_rule.updated · alert_rule.deleted
//   alert_rule.activation_updated · alert_rule.thresholds_updated
//   notification_channel.created · notification_channel.updated
//   notification_channel.deleted
//   config.updated · config.retry_requested
//   invoice.action_requested · invoice.pdf_url_issued
//   remediation.<slug> (reset_retry_budget, retry_binding_repair,
//     retry_retired_ad_pause, force_audience_refresh, release_ttd_drop_intent,
//     remint_meta_container, repin_failed_publish)
//   pipeline.pii_revealed · meta_custom_conversions.reconciled
//   production.alerts_exempt_set · venue.seats_overridden
//   api_key.created · api_key.revoked · oauth_grant.revoked
// There is no `role.*` or `token.*` action: a role change is `user.patched`
// with a `role` entry in its diff. An entry made with an API key or through Claude
// names it in `via`, whatever its action (the audit screen's chip).

/** The icons an audit row can carry; each is a key of `Icons`. */
export type AuditIcon =
  | 'plus' | 'send' | 'check' | 'key' | 'play' | 'pause' | 'trash' | 'x'
  | 'refresh' | 'history' | 'shield' | 'dl' | 'settings' | 'more';

/**
 * `danger` — destructive: red. `neutral` — a routine people/access event:
 * neutral ink. `family` — everything else keeps its family's colour.
 */
export type AuditTone = 'danger' | 'neutral' | 'family';

/** The parts of an audit entry its presentation reads. */
export interface AuditActionInput {
  action: string;
  diff: ReadonlyArray<{ field: string; before: string; after: string }> | null;
}

/** `campaign` for `campaign.paused`. */
export function auditFamily(action: string): string {
  return action.split('.')[0] ?? action;
}

/** `paused` for `campaign.paused`; everything after the first dot. */
function auditVerb(action: string): string {
  const dot = action.indexOf('.');
  return dot < 0 ? '' : action.slice(dot + 1);
}

function diffAfter(input: AuditActionInput, field: string): string | undefined {
  return input.diff?.find((d) => d.field === field)?.after;
}

/** A `user.patched` row that changed the user's role. */
function changesRole(input: AuditActionInput): boolean {
  return input.action === 'user.patched' && diffAfter(input, 'role') !== undefined;
}

/** A `user.patched` row that set the user's status to `status`. */
function setsStatus(input: AuditActionInput, status: string): boolean {
  return input.action === 'user.patched' && diffAfter(input, 'status') === status;
}

/**
 * Destructive: something was removed, switched off, or stopped working for
 * someone. Deleting or disabling, a role change, a revoked key or link, and a
 * server-key rotation, which invalidates the previous key at once.
 */
/** Families whose spelled-out form would read wrong ("Api key", "Oauth grant"). */
const FAMILY_LABEL: ReadonlyMap<string, string> = new Map([
  ['api_key', 'API key'],
  ['oauth_grant', 'connected app'],
]);

/** A family as the ON filter names it: `alert_rule` → "Alert rule". */
export function actionFamilyLabel(family: string): string {
  const named = FAMILY_LABEL.get(family);
  if (named) return named;
  const words = family.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function isDestructiveAuditAction(input: AuditActionInput): boolean {
  const verb = auditVerb(input.action);
  if (verb === 'deleted' || verb === 'disabled' || verb === 'role_changed') return true;
  if (verb === 'revoked' || verb.endsWith('_revoked')) return true;
  if (input.action === 'pixel.server_key_rotated') return true;
  return changesRole(input) || setsStatus(input, 'disabled');
}

export function auditActionTone(input: AuditActionInput): AuditTone {
  if (isDestructiveAuditAction(input)) return 'danger';
  const family = auditFamily(input.action);
  return family === 'user' || family === 'role' ? 'neutral' : 'family';
}

const ICON_BY_VERB: ReadonlyMap<string, AuditIcon> = new Map(Object.entries({
  // Something new.
  created: 'plus',
  duplicated: 'plus',
  revision_created: 'plus',
  invited: 'plus',
  asset_attached: 'plus',
  creative_added: 'plus',
  server_key_minted: 'plus',
  share_link_issued: 'plus',
  review_link_issued: 'plus',
  review_package_issued: 'plus',
  // An edit, or something sent on.
  updated: 'send',
  patched: 'send',
  plan_updated: 'send',
  content_saved: 'send',
  conversions_updated: 'send',
  activation_updated: 'send',
  thresholds_updated: 'send',
  seats_overridden: 'send',
  alerts_exempt_set: 'send',
  asset_detached: 'send',
  submitted_for_review: 'send',
  creative_rollout_requested: 'send',
  invite_resent: 'send',
  action_requested: 'send',
  // Accepted, approved, settled.
  invite_accepted: 'check',
  approved: 'check',
  acked: 'check',
  resolved: 'check',
  reconciled: 'check',
  // Access.
  password_reset_issued: 'key',
  password_reset_completed: 'key',
  pii_revealed: 'shield',
  role_changed: 'shield',
  // Running and stopped.
  enabled: 'play',
  resumed: 'play',
  ready_to_publish: 'play',
  auto_launch_armed: 'play',
  paused: 'pause',
  auto_launch_disarmed: 'pause',
  // Gone.
  archived: 'trash',
  deleted: 'trash',
  disabled: 'x',
  revoked: 'x',
  share_revoked: 'x',
  review_link_revoked: 'x',
  server_key_revoked: 'x',
  creative_addition_cancelled: 'x',
  // Asked again.
  sync_retry_requested: 'refresh',
  retry_requested: 'refresh',
  refresh_requested: 'refresh',
  meta_previews_refreshed: 'refresh',
  creative_addition_retried: 'refresh',
  server_key_rotated: 'refresh',
  restored: 'history',
  pdf_url_issued: 'dl',
} satisfies Record<string, AuditIcon>));

export function auditActionIcon(input: AuditActionInput): AuditIcon {
  if (changesRole(input)) return 'shield';
  if (setsStatus(input, 'disabled')) return 'x';
  if (setsStatus(input, 'active')) return 'play';
  const family = auditFamily(input.action);
  if (family === 'remediation' || family === 'config') {
    return auditVerb(input.action) === 'retry_requested' ? 'refresh' : 'settings';
  }
  // A Map, not an object lookup: `campaign.constructor` must not find `Object`.
  return ICON_BY_VERB.get(auditVerb(input.action)) ?? 'more';
}
